import {
  CenteredLoading,
  CustomDialogTitle,
  CustomInputField,
  CustomSelect,
} from "@/features/ui";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Grid,
} from "@mui/material";
import PropTypes from "prop-types";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { ParentChartOfAccountSelect } from "../parent-chart-of-account-select";
import {
  ChartOfAccountTypeEnum,
  chartOfAccountTypeLabels,
} from "../chart-of-account-type";
import {
  DEFAULT_VALUES,
  VALIDATION_SCHEMA,
} from "./chart-of-account-form-constants";

export const ChartOfAccountFormDialog = ({
  defaultValues,
  onSubmit,
  onClose,
  dialogTitle,
  renderDialogActionsChildren,
  isLoading = false,
}) => {
  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    formState: { isSubmitting, errors },
  } = useForm({
    defaultValues: DEFAULT_VALUES,
    resolver: yupResolver(VALIDATION_SCHEMA),
  });

  const hasParent = watch("hasParent");
  const autoGenerateCode = watch("autoGenerateCode");
  const selectedType = watch("type");

  const excludeSubtreeId = getValues("id");

  // Check if we're in edit mode (has an ID)
  const isEditMode = Boolean(excludeSubtreeId);

  // When adding a child from a parent row (NOT edit mode), lock type, hasParent, and parent fields
  const isAddingChildFromParent =
    Boolean(defaultValues?.parentChartOfAccount) && !isEditMode;

  // Parent is locked when adding a child with pre-filled parent
  const isParentLocked = isAddingChildFromParent;

  useEffect(() => {
    if (defaultValues) reset({ ...DEFAULT_VALUES, ...defaultValues });
  }, [defaultValues, reset]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const onFormSubmit = async (formData) => {
    const payload = {
      name: formData.name,
      type: formData.type,
      isActive: formData.isActive,
    };

    // Only include code if auto-generation is disabled
    if (!formData.autoGenerateCode) {
      payload.code = formData.code;
    }

    // Only include parentId if hasParent is checked and parent exists
    if (formData.hasParent && formData.parentChartOfAccount?.id) {
      payload.parentId = formData.parentChartOfAccount.id;
    }

    await onSubmit(payload);
  };

  return (
    <Dialog open onClose={handleClose} fullWidth maxWidth="md">
      <CustomDialogTitle
        variant="h5"
        title={dialogTitle}
        onClose={handleClose}
      />
      {isLoading ? (
        <CenteredLoading sx={{ height: "25rem" }} />
      ) : (
        <Box component="form" onSubmit={handleSubmit(onFormSubmit)} noValidate>
          <DialogContent>
            <Grid container spacing={2.5}>
              {/* Tipe Akun */}
              <Grid size={{ xs: 12, md: 4 }}>
                <Controller
                  control={control}
                  name="type"
                  render={({ field }) => (
                    <CustomSelect
                      required
                      id="type"
                      label="Tipe Akun"
                      options={Object.values(ChartOfAccountTypeEnum).map(
                        (value) => ({
                          value: value,
                          label: chartOfAccountTypeLabels[value],
                        }),
                      )}
                      value={field.value}
                      onChange={(value) => {
                        field.onChange(value);
                        // Clear parent selection when type changes (unless parent is locked)
                        if (!isParentLocked) {
                          setValue("parentChartOfAccount", null);
                        }
                      }}
                      helperText={errors.type?.message}
                      error={Boolean(errors.type)}
                      disabled={isEditMode || isAddingChildFromParent}
                    />
                  )}
                />
              </Grid>

              {/* Merupakan Sub */}
              <Grid size={{ xs: 12, md: 2 }}>
                <Controller
                  control={control}
                  name="hasParent"
                  render={({ field, fieldState }) => (
                    <CustomSelect
                      required
                      value={field.value}
                      onChange={field.onChange}
                      id="hasParent"
                      label="Merupakan Sub"
                      options={[
                        { value: true, label: "Ya" },
                        { value: false, label: "Tidak" },
                      ]}
                      error={fieldState.invalid}
                      helperText={fieldState.error?.message}
                      disabled={isAddingChildFromParent}
                    />
                  )}
                />
              </Grid>

              {/* Turunan dari - only show content when hasParent is true */}
              <Grid size={{ xs: 12, md: 6 }}>
                {hasParent && (
                  <Controller
                    control={control}
                    name="parentChartOfAccount"
                    render={({ field, fieldState }) => (
                      <ParentChartOfAccountSelect
                        value={field.value}
                        onChange={field.onChange}
                        disabled={isParentLocked || !selectedType}
                        accountType={selectedType}
                        excludeSubtreeId={excludeSubtreeId}
                        TextFieldProps={{
                          required: hasParent,
                          label: "Turunan dari",
                          error: fieldState.invalid,
                          helperText:
                            !selectedType && !isParentLocked
                              ? "Pilih tipe akun terlebih dahulu"
                              : fieldState.error?.message,
                          InputProps: {
                            placeholder: "Pilih Akun",
                          },
                        }}
                      />
                    )}
                  />
                )}
              </Grid>

              {/* Auto Generate Code Checkbox - only in create mode */}
              {!isEditMode && (
                <Grid size={12}>
                  <Controller
                    control={control}
                    name="autoGenerateCode"
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={field.value}
                            onChange={field.onChange}
                          />
                        }
                        label="Kode Otomatis"
                      />
                    )}
                  />
                </Grid>
              )}

              {/* Kode - only show when autoGenerateCode is false */}
              {!autoGenerateCode && (
                <Grid size={{ xs: 12, md: 3 }}>
                  <CustomInputField
                    required
                    name="code"
                    label="Kode"
                    register={register}
                    helperText={errors.code?.message}
                    error={Boolean(errors.code)}
                  />
                </Grid>
              )}

              {/* Nama */}
              <Grid size={{ xs: 12, md: !autoGenerateCode ? 6 : 9 }}>
                <CustomInputField
                  required
                  name="name"
                  label="Nama"
                  register={register}
                  helperText={errors.name?.message}
                  error={Boolean(errors.name)}
                />
              </Grid>

              {/* Status */}
              <Grid size={{ xs: 12, md: 3 }}>
                <Controller
                  control={control}
                  name="isActive"
                  render={({ field, fieldState }) => (
                    <CustomSelect
                      required
                      value={field.value}
                      onChange={field.onChange}
                      id="isActive"
                      label="Status"
                      options={[
                        { value: true, label: "Aktif" },
                        { value: false, label: "Nonaktif" },
                      ]}
                      error={fieldState.invalid}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            {renderDialogActionsChildren({ isLoading: isSubmitting })}
          </DialogActions>
        </Box>
      )}
    </Dialog>
  );
};

ChartOfAccountFormDialog.propTypes = {
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  dialogTitle: PropTypes.string.isRequired,
  renderDialogActionsChildren: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};
